import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Circle, AlertCircle, MinusCircle, Info } from 'lucide-react';
import { getMyWarmupStatus } from '../../services/api/ocel.api';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { cn } from '../../utils/cn';

// Poll while the popover is open so status transitions (cold -> warming ->
// warm) show up live without the user having to reopen the panel.
const POLL_MS = 5000;

const PASS_STATUS_LABEL = {
  running: 'Aufwärmen läuft…',
  completed: 'Fertig',
  unknown: 'Unbekannt',
};

const TILE_STATUS_META = {
  warm: {
    icon: CheckCircle2,
    className: 'text-green-500',
    label: 'Warm',
    tooltip: (tile) => {
      const elapsed = tile.targets?.[0]?.elapsed;
      return `Warm — alle zugehörigen Ansichten vorberechnet${elapsed != null ? ` (${elapsed.toFixed(2)}s)` : ''}`;
    },
  },
  warming: {
    icon: Loader2,
    className: 'text-amber-500 animate-spin',
    label: 'Wird aufgewärmt',
    tooltip: () => 'Wird gerade aufgewärmt…',
  },
  cold: {
    icon: Circle,
    className: 'text-blue-400',
    label: 'Kalt',
    tooltip: () => 'Noch nicht aufgewärmt — wird beim nächsten Durchlauf berücksichtigt',
  },
  error: {
    icon: AlertCircle,
    className: 'text-red-500',
    label: 'Fehler',
    tooltip: (tile) => {
      const err = tile.targets?.find((t) => t.error)?.error;
      return err ? `Fehler beim Aufwärmen: ${err}` : 'Fehler beim Aufwärmen';
    },
  },
  no_mapper: {
    icon: MinusCircle,
    className: 'text-gray-300',
    label: 'Nicht unterstützt',
    tooltip: () => 'Diese Kachel wird derzeit nicht unterstützt (kein Warm-up verfügbar)',
  },
  zero_targets: {
    icon: MinusCircle,
    className: 'text-gray-300',
    label: 'Unvollständig',
    tooltip: () => 'Konfiguration unvollständig — nichts zum Vorberechnen',
  },
};

// Informational only — a file can already be warm right now (e.g. via a
// one-off manual/upload/lazy trigger) while still not being one future
// automatic passes will keep re-warming. Shown alongside the tile list below,
// never in place of it.
const NOT_APPLICABLE_BANNER = {
  stale_file: 'Diese Datei ist nicht mehr der aktuell aktive Datensatz — zukünftige automatische Durchläufe wärmen sie nicht erneut auf.',
  other_tenant: 'Diese Datei gehört zu einem anderen Mandanten — Ihre gespeicherte Konfiguration wird von zukünftigen automatischen Durchläufen nicht berücksichtigt.',
};

/**
 * Detailed, per-tile cache warm-up status for the CURRENT user's own saved
 * dashboard on activeFile (see backend GET .../warm-status/mine). Polls only
 * while `open` (rendered inside WarmupStatusIndicator's Popover).
 */
const WarmupStatusPanel = ({ activeFile, open }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !activeFile) return undefined;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const res = await getMyWarmupStatus(activeFile);
        if (cancelled) return;
        setData(res);
        setError(false);
      } catch {
        if (!cancelled) setError(true);
      }
      if (!cancelled) timer = setTimeout(load, POLL_MS);
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, activeFile]);

  if (error) {
    return <div className="p-4 text-xs text-gray-500">Status konnte nicht geladen werden.</div>;
  }
  if (!data) {
    return <div className="p-4 text-xs text-gray-500">Lade Status…</div>;
  }
  if (!data.config_saved) {
    return <div className="p-4 text-xs text-gray-500">Kein gespeichertes Dashboard für diesen Benutzer.</div>;
  }

  const banner = data.not_applicable_reason ? NOT_APPLICABLE_BANNER[data.not_applicable_reason] : null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-700">Cache-Status Ihrer Kacheln</span>
          <Badge variant={data.pass_status === 'running' ? 'secondary' : 'outline'}>
            {PASS_STATUS_LABEL[data.pass_status] || data.pass_status}
            {data.progress ? ` (${data.progress.completed}/${data.progress.total})` : ''}
          </Badge>
        </div>

        {banner && (
          <div className="flex items-start gap-2 px-4 py-2 text-xs text-gray-600 bg-amber-50">
            <Info className="w-4 h-4 flex-shrink-0 text-amber-500 mt-0.5" />
            <span>{banner}</span>
          </div>
        )}
        <ScrollArea className="h-80">
            <ul className="divide-y divide-gray-100">
              {data.tiles.map((tile) => {
                const meta = TILE_STATUS_META[tile.status] || TILE_STATUS_META.no_mapper;
                const Icon = meta.icon;
                return (
                  <li key={tile.instance_id} className="flex items-center gap-2 px-4 py-2 text-xs">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-2 flex-1 min-w-0 cursor-default">
                          <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', meta.className)} />
                          <span className="truncate text-gray-700">
                            {tile.component_name || tile.component_id || tile.instance_id}
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">{meta.tooltip(tile)}</TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
              {data.tiles.length === 0 && (
                <li className="px-4 py-3 text-xs text-gray-400">Keine Kacheln konfiguriert.</li>
              )}
            </ul>
        </ScrollArea>
      </div>
    </TooltipProvider>
  );
};

export default WarmupStatusPanel;
